import { makeAutoObservable, runInAction } from 'mobx';
import { 
  BaseModalContent, 
  ModalContentType, 
  PrivacyPolicyResponse, 
  NewsletterResponse, 
  DisclaimerResponse,
  ModalContentResponse,
  ModalContentTypeKey,
  OfferResponse
} from '../model/types';
import { axiosInstance } from 'shared/api/axios-instance';

class ModalContentStore {
  content: ModalContentType = {
    privacyPolicy: null,
    termsOfService: null,
    newsletter: null,
    offer: null,
  };

  loading: Record<ModalContentTypeKey, boolean> = {
    privacyPolicy: false,
    termsOfService: false,
    newsletter: false,
    offer: false,
  };

  errors: Record<ModalContentTypeKey, string | null> = {
    privacyPolicy: null,
    termsOfService: null,
    newsletter: null,
    offer: null,
  };

  constructor() {
    makeAutoObservable(this);
  }

  // Computed properties for better performance
  get isAnyLoading(): boolean {
    return Object.values(this.loading).some(loading => loading);
  }

  get hasAnyError(): boolean {
    return Object.values(this.errors).some(error => error !== null);
  }

  setContent(type: ModalContentTypeKey, content: BaseModalContent): void {
    this.content[type] = content;
  }

  setLoading(type: ModalContentTypeKey, loading: boolean): void {
    this.loading[type] = loading;
  }

  setError(type: ModalContentTypeKey, error: string | null): void {
    this.errors[type] = error;
  }

  getContent(type: ModalContentTypeKey): BaseModalContent | null {
    return this.content[type];
  }

  isLoading(type: ModalContentTypeKey): boolean {
    return this.loading[type];
  }

  getError(type: ModalContentTypeKey): string | null {
    return this.errors[type];
  }

  private processResponse(type: ModalContentTypeKey, data: ModalContentResponse): BaseModalContent {
    const baseContent = {
      notification_count: data.notification_count,
      expired: data.expired || [], // Ensure expired is always an array
    };

    switch (type) {
      case 'privacyPolicy': {
        const privacyData = data as PrivacyPolicyResponse;
        return {
          ...baseContent,
          title: privacyData.privacy_policy.title,
          content: privacyData.privacy_policy.content,
        };
      }
      case 'offer': {
        const offerData = data as OfferResponse;
        return {
          ...baseContent,
          title: offerData.offer.title,
          content: offerData.offer.content,
        };
      }
      case 'termsOfService': {
        const disclaimerData = data as DisclaimerResponse;
        return {
          ...baseContent,
          title: disclaimerData.disclaimer.title,
          content: disclaimerData.disclaimer.content,
        };
      }
      case 'newsletter': {
        const newsletterData = data as NewsletterResponse;
        return {
          ...baseContent,
          title: newsletterData.agreement.title,
          content: newsletterData.agreement.content,
        };
      }
      default:
        throw new Error(`Unsupported modal content type: ${type}`);
    }
  }

  async fetchContent(type: ModalContentTypeKey): Promise<BaseModalContent | null> {
    // Return cached content if available and not expired
    const cachedContent = this.content[type];
    if (cachedContent && !this.isContentExpired(cachedContent)) {
      return cachedContent;
    }

    const endpoints: Record<ModalContentTypeKey, string> = {
      privacyPolicy: '/api/privacy-policy/',
      termsOfService: '/api/disclaimer/',
      newsletter: '/api/newsletter-agreement/',
      offer: '/api/offer/',
    };

    try {
      runInAction(() => {
        this.setLoading(type, true);
        this.setError(type, null);
      });

      const response = await axiosInstance.get(endpoints[type]);
      
      if (!response.data) {
        throw new Error('No data received from server');
      }

      const processedContent = this.processResponse(type, response.data);

      runInAction(() => {
        this.setContent(type, processedContent);
        this.setLoading(type, false);
      });

      return processedContent;
    } catch (error) {
      console.error(`Error fetching ${type} content:`, error);
      
      const errorMessage = this.getErrorMessage(error);
      
      runInAction(() => {
        this.setError(type, errorMessage);
        this.setLoading(type, false);
      });

      // Return mock data for development/testing
      // if (process.env.NODE_ENV === 'development') {
      //   const mockContent = this.getMockContent(type);
      //   if (mockContent) {
      //     runInAction(() => {
      //       this.setContent(type, mockContent);
      //     });
      //     return mockContent;
      //   }
      // }
      
      return null;
    }
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'An unknown error occurred';
  }

  private isContentExpired(content: BaseModalContent): boolean {
    // Add your expiration logic here if needed
    // For now, content is considered valid
    return false;
  }

  // private getMockContent(type: ModalContentTypeKey): BaseModalContent | null {
  //   const mockData: Record<ModalContentTypeKey, BaseModalContent> = {
  //     privacyPolicy: {
  //       title: "ПОЛІТИКА КОНФІДЕНЦІЙНОСТІ",
  //       content: "Ваша конфіденційність дуже важлива для нас!\n\nМи створили Політику конфіденційності, щоб продемонструвати свою вірність принципам конфіденційності та безпеки...",
  //       notification_count: 2,
  //       expired: []
  //     },
  //     termsOfService: {
  //       title: "УМОВИ ВИКОРИСТАННЯ", 
  //       content: "Умови використання нашого сервісу...",
  //       notification_count: 1,
  //       expired: []
  //     },
  //     newsletter: {
  //       title: "УГОДА ПРО РОЗСИЛКУ",
  //       content: "Угода про підписку на розсилку...",
  //       notification_count: 0,
  //       expired: []
  //     }
  //   };

  //   return mockData[type] || null;
  // }

  // Method to clear cache and refetch content
  async refreshContent(type: ModalContentTypeKey): Promise<BaseModalContent | null> {
    runInAction(() => {
      this.content[type] = null;
      this.setError(type, null);
    });
    
    return this.fetchContent(type);
  }

  // Method to refresh all content
  async refreshAllContent(): Promise<void> {
    const types: ModalContentTypeKey[] = ['privacyPolicy', 'termsOfService', 'newsletter'];
    await Promise.all(types.map(type => this.refreshContent(type)));
  }

  // Method to clear all cached content
  clearCache(): void {
    runInAction(() => {
      this.content = {
        privacyPolicy: null,
        termsOfService: null,
        newsletter: null,
        offer: null,
      };
      this.errors = {
        privacyPolicy: null,
        termsOfService: null,
        newsletter: null,
        offer: null,
      };
      this.loading = {
        privacyPolicy: false,
        termsOfService: false,
        newsletter: false,
        offer: false,
      };
    });
  }

  // Method to preload all content
  async preloadAllContent(): Promise<void> {
    const types: ModalContentTypeKey[] = ['privacyPolicy', 'termsOfService', 'newsletter'];
    await Promise.all(types.map(type => this.fetchContent(type)));
  }
}

export const modalContentStore = new ModalContentStore();